import {globals} from '@extensions/globals-base'
import {
  Hub,
  SandboxTree,
  StickerStateId,
  stickerTypeProperty,
  Tag
} from 'yougile-model'
import {logger, Util} from 'yougile-util'
import {AStickerCtl} from './a-sticker-ctl'

export interface StickerState {
  id: StickerStateId
}

/**
 * Stickers String and Sprint have a user-defined set of states.
 * Each state have its is (StickerStateId) this id is used as a
 * sticker value on tasks.
 */
export abstract class ACustomStickerCtl<
  T extends StickerState
> extends AStickerCtl<StickerStateId> {
  makeShortId(): StickerStateId {
    return Util.makeShortId() as StickerStateId
  }

  make(box: SandboxTree): Tag {
    const sticker = new Tag(box.companyId, '')
    sticker.properties[stickerTypeProperty] = this.type
    sticker.data.by = globals.currentUserId
    return sticker
  }

  addState(sticker: Tag, state: T): void {
    sticker.states.index[state.id] = state
    sticker.states.order.push(state.id)
    sticker.commit()
  }

  changeState(sticker: Tag, id: StickerStateId, state: T): void {
    if (!sticker.states.index[id]) {
      logger.warn('State not found', id)
      return
    }
    state.id = id
    sticker.states.index[id] = state
    sticker.commit()
  }

  removeState(sticker: Tag, id: StickerStateId): void {
    delete sticker.states.index[id]
    const ix = sticker.states.order.indexOf(id)
    if (ix > -1) {
      sticker.states.order.splice(ix, 1)
    }
    sticker.commit()
  }

  getState(sticker: Tag, id: StickerStateId): T {
    return Util.clone(sticker.states.index[id]) as T
  }

  listStates(sticker: Tag): T[] {
    const allKeys = Object.keys(sticker.states.index)
    const uniqueOrdered = new Set(sticker.states.order)
    const keys = [
      ...uniqueOrdered,
      ...allKeys.filter(k => !uniqueOrdered.has(k))
    ]

    return keys.map(id => sticker.states.index[id] as T).filter(s => !!s)
  }

  // TODO: remove
  cleanOldState(sticker: Tag, task: Hub): void {
    const id = task.stickers[sticker.id]
    if (!!id && !sticker.states.index[id]) {
      task.stickers[sticker.id] = ''
      task.commit()
    }
  }

  displayedTitle(tag: Tag): string {
    return tag.title
  }

  abstract makeNewState(): T
}
